import { RetryableDonationFormInvariantError } from "../errors"
import { createDonation, createRecurringDonation } from "../queries"
import {
  CreateDonationPayload,
  CreateRecurringDonationPayload,
  DateStampString,
  Designation,
  DonationDesignationResource,
  PayloadMeta,
  FormSubmissionResult,
  RecurringDonationDesignationResource,
  RepeatableFrequency,
  Schedule,
  State,
  StripeToken,
} from "../types"
import { shouldSavePaymentMethod } from "./paymentMethods"
import { frequencyToSchedule } from "./frequency"

type StateForFormSubmission = Pick<
  State,
  | "frequency"
  | "stripePaymentMethod"
  | "savePaymentMethodChecked"
  | "designations"
  | "feeCovered"
  | "memoChecked"
  | "memoText"
> & {
  memoEligible: boolean
}

type FormSubmissionParams = {
  state: StateForFormSubmission
  calculatedDesignations: Designation[]
  stripeToken: StripeToken
  recaptchaCode?: string
  embedded?: boolean
}

export abstract class FormSubmission<Payload> {
  protected state: StateForFormSubmission
  protected calculatedDesignations: Designation[]
  protected stripeToken: StripeToken
  protected recaptchaCode?: string
  protected embedded?: boolean
  protected payload?: Payload

  constructor({
    state,
    calculatedDesignations,
    stripeToken,
    recaptchaCode,
    embedded,
  }: FormSubmissionParams) {
    this.state = state
    this.calculatedDesignations = calculatedDesignations
    this.stripeToken = stripeToken
    this.recaptchaCode = recaptchaCode
    this.embedded = embedded
    this.buildPayload()
  }

  static create(
    params: FormSubmissionParams,
  ): OneTimeFormSubmission | RecurringFormSubmission {
    const { state } = params
    if (state.frequency.type === "once") {
      return new OneTimeFormSubmission(params)
    } else {
      return new RecurringFormSubmission(params)
    }
  }

  protected buildPayload(): void {}

  abstract submit(): Promise<FormSubmissionResult>

  protected buildPayloadMetaDebug(): PayloadMeta {
    const meta: PayloadMeta = {
      debug_original_designations: this.state.designations,
    }
    if (this.state.stripePaymentMethod?.type) {
      meta.debug_stripe_method_type = this.state.stripePaymentMethod.type
    }
    return meta
  }

  protected get sharedAttributes() {
    const shouldIncludeMemo =
      this.state.memoChecked &&
      this.state.memoEligible &&
      this.state.memoText.trim().length > 0
    return {
      fee_covered: this.state.feeCovered,
      stripe_token: this.stripeToken,
      source: "web" as const,
      ...(this.embedded && { embedded: true }),
      ...(shouldIncludeMemo && { memo: this.state.memoText.trim() }),
    }
  }
}

class OneTimeFormSubmission extends FormSubmission<CreateDonationPayload> {
  async submit() {
    if (!this.payload) {
      throw new RetryableDonationFormInvariantError()
    } else {
      return await createDonation({ payload: this.payload })
    }
  }

  protected buildPayload() {
    this.payload = {
      data: {
        attributes: {
          ...this.sharedAttributes,
          ...(this.recaptchaCode && { recaptcha_code: this.recaptchaCode }),
          store_payment_method: shouldSavePaymentMethod({
            frequency: this.state.frequency,
            stripePaymentMethod: this.state.stripePaymentMethod,
            savePaymentMethodChecked: this.state.savePaymentMethodChecked,
          }),
        },
      },
      included: this.calculatedDesignations.map(this.designationToResource),
      meta: this.buildPayloadMetaDebug(),
    }
  }

  private designationToResource(
    designation: Designation,
  ): DonationDesignationResource {
    return {
      type: "Designation",
      attributes: { amount: designation.amount },
      relationships: {
        fund: {
          data: {
            type: "Fund",
            id: designation.fundId,
          },
        },
      },
    }
  }
}

class RecurringFormSubmission extends FormSubmission<CreateRecurringDonationPayload> {
  async submit() {
    if (!this.payload) {
      throw new RetryableDonationFormInvariantError()
    } else {
      return await createRecurringDonation({ payload: this.payload })
    }
  }

  protected buildPayload(): void {
    this.payload = {
      data: {
        attributes: {
          ...this.sharedAttributes,
          schedule: this.schedule,
          next_occurrence: this.nextOccurrence,
        },
      },
      included: this.calculatedDesignations.map(this.designationToResource),
      meta: this.buildPayloadMetaDebug(),
    }
  }

  private get schedule(): Schedule {
    return frequencyToSchedule(this.state.frequency as RepeatableFrequency)
  }

  private get nextOccurrence(): DateStampString {
    return (this.state.frequency as RepeatableFrequency).starting
  }

  private designationToResource(
    designation: Designation,
  ): RecurringDonationDesignationResource {
    return {
      type: "RecurringDonationDesignation",
      attributes: { amount: designation.amount },
      relationships: {
        fund: {
          data: {
            type: "Fund",
            id: designation.fundId,
          },
        },
      },
    }
  }
}
